import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Paperclip, Download, Send, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';


export default function CommunicationDetailPage() {
  const { commId } = useParams();
  const navigate = useNavigate();
  const { user, isCompany, isWorker } = useAuth();
  const [communication, setCommunication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchCommunication();
  }, [commId]);

  const fetchCommunication = async () => {
    try {
      const response = await API.get(`/communications/${commId}`);
      setCommunication(response.data);
    } catch (err) {
      console.error('Error fetching communication:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await API.get(`/communications/${commId}/attachment/${attachment.id}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;

    setSending(true);
    try {
      await API.post(`/communications/${commId}/reply`, {
        content: replyContent
      });
      setReplyContent('');
      fetchCommunication();
    } catch (err) {
      console.error('Error sending reply:', err);
    } finally {
      setSending(false);
    }
  };

  const backPath = isCompany ? '/company/communications' : '/worker/communications';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-100 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!communication) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Comunicado no encontrado</p>
        <Button variant="link" onClick={() => navigate(backPath)}>
          Volver a comunicados
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="communication-detail-page">
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={() => navigate(backPath)}
        className="text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver a comunicados
      </Button>

      {/* Communication */}
      <div className="bg-white border border-orange-100 rounded-xl">
        <div className="p-6 border-b border-orange-100">
          <h1 className="text-2xl font-bold text-gray-900" >
            {communication.subject}
          </h1>
          <p className="text-sm text-gray-400 mt-2 font-mono">
            {new Date(communication.created_at).toLocaleDateString('es-ES', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        </div>
        
        <div className="p-6">
          <p className="text-gray-700 whitespace-pre-wrap">{communication.content}</p>
          
          {/* Attachments */}
          {communication.attachments && communication.attachments.length > 0 && (
            <div className="mt-6 pt-6 border-t border-orange-100">
              <h3 className="text-xs uppercase tracking-wider font-semibold text-gray-500 mb-3">
                Adjuntos ({communication.attachments.length})
              </h3>
              <div className="space-y-2">
                {communication.attachments.map((att) => (
                  <div 
                    key={att.id}
                    className="flex items-center justify-between p-3 bg-[#FFF8F4] rounded-md"
                  >
                    <div className="flex items-center gap-2">
                      <Paperclip className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-700">{att.original_filename}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownloadAttachment(att)}
                      data-testid={`download-attachment-${att.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Replies */}
      <div className="bg-white border border-orange-100 rounded-xl">
        <div className="p-6 border-b border-orange-100">
          <h2 className="text-lg font-bold text-gray-900" >
            Respuestas ({communication.replies?.length || 0})
          </h2>
        </div>

        {/* Reply list */}
        {communication.replies && communication.replies.length > 0 && (
          <div className="divide-y divide-orange-100">
            {communication.replies.map((reply) => (
              <div key={reply.id} className="p-6" data-testid={`reply-${reply.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-orange-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{reply.user_name}</span>
                      <span className="text-xs text-gray-400 bg-orange-50 px-2 py-0.5 rounded">
                        {reply.user_role === 'company' ? 'Empresa' : 'Trabajador'}
                      </span>
                    </div>
                    <p className="text-gray-700 mt-2 whitespace-pre-wrap">{reply.content}</p>
                    <p className="text-xs text-gray-400 mt-2 font-mono">
                      {new Date(reply.created_at).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Reply form */}
        <div className="p-6 border-t border-orange-100 bg-[#FFF8F4]">
          <form onSubmit={handleSendReply}>
            <Textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Escribe tu respuesta..."
              rows={3}
              className="mb-4"
              data-testid="reply-content-input"
            />
            <div className="flex justify-end">
              <Button 
                type="submit" 
                disabled={sending || !replyContent.trim()}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-md shadow-orange-200"
                data-testid="send-reply-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Enviando...' : 'Enviar respuesta'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
